import React from "react";
import { BookUser, Globe, type LucideIcon } from "lucide-react";
import { cn } from "@/utils";

/** Одноцветные знаки сервисов в /logo/mono (общий набор с osint-tools). */
const MONO = new Set([
  "adobe",
  "aliexpress",
  "amazon",
  "apple",
  "archive",
  "cashapp",
  "cf-hero",
  "chesscom",
  "crypto",
  "discord",
  "duolingo",
  "ebay",
  "envato",
  "evernote",
  "eyecon",
  "facebook",
  "firefox",
  "flickr",
  "fotostrana",
  "garmin",
  "github",
  "google",
  "gosuslugi",
  "gravatar",
  "hubspot",
  "icq",
  "imgur",
  "instagram",
  "komoot",
  "lastfm",
  "lastpass",
  "linkedin",
  "mailru",
  "max",
  "medium",
  "microsoft",
  "myspace",
  "nike",
  "numbuster",
  "office365",
  "ok",
  "patreon",
  "phone",
  "pinterest",
  "pochta-rossii",
  "profile-analyzer",
  "protonmail",
  "quora",
  "reddit",
  "replit",
  "runkeeper",
  "russian-railways",
  "samsung",
  "skype",
  "snapchat",
  "soundcloud",
  "spotify",
  "steam",
  "strava",
  "telegram",
  "tenchat",
  "tiktok",
  "tumblr",
  "twitter",
  "venmo",
  "viber",
  "vivino",
  "vk",
  "vsco",
  "walmart",
  "whatsapp",
  "wordpress",
  "xing",
  "yandex",
  "zoho",
]);

/** Синонимы ключей источников → имя файла знака. */
const ALIASES: Record<string, string> = {
  odnoklassniki: "ok",
  vkontakte: "vk",
  x: "twitter",
  "mail.ru": "mailru",
  rzd: "russian-railways",
  cf_hero: "cf-hero",
  chess: "chesscom",
  "yandex.ru": "yandex",
  ya: "yandex",
  "chess.com": "chesscom",
};

/** Сервисы без векторного знака (у GetContact в исходнике только растр). */
const FALLBACK_GLYPHS: Record<string, LucideIcon> = {
  getcontact: BookUser,
};

export function serviceGlyphName(service: string): string | null {
  const key = service.trim().toLowerCase();
  const name = ALIASES[key] ?? key;
  return MONO.has(name) ? name : null;
}

interface ServiceGlyphProps {
  service: string;
  /** Размер в пикселях; без него размер задает className. */
  size?: number;
  className?: string;
  /** Знак для сервиса без собственного логотипа. */
  fallback?: LucideIcon;
  title?: string;
}

/**
 * Знак стороннего сервиса — всегда одного цвета с текстом вокруг.
 *
 * Логотипы в /logo/mono одноцветные; <img> не наследует цвет, поэтому знак
 * рисуется CSS-маской поверх currentColor. Бренд-градиенты мессенджеров и
 * соцсетей больше не красят интерфейс: цвет в системе означает риск, а не
 * принадлежность к платформе.
 */
const ServiceGlyph: React.FC<ServiceGlyphProps> = ({
  service,
  size = 16,
  className,
  fallback: DefaultFallback = Globe,
  title,
}) => {
  const name = serviceGlyphName(service);
  if (!name) {
    const Fallback = FALLBACK_GLYPHS[service.trim().toLowerCase()] ?? DefaultFallback;
    return (
      <Fallback
        size={size}
        strokeWidth={1.75}
        className={cn("inline-block shrink-0", className)}
        aria-hidden={!title}
        aria-label={title}
      />
    );
  }
  const mask = `url(/logo/mono/${name}.svg) center / contain no-repeat`;
  return (
    <span
      role={title ? "img" : undefined}
      aria-label={title}
      aria-hidden={!title}
      className={cn("inline-block shrink-0", className)}
      style={{ width: size, height: size, background: "currentColor", WebkitMask: mask, mask }}
    />
  );
};

export default ServiceGlyph;
